import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild
} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {TranslationService} from '../../../core/services/translation.service';
import {BaseDataService} from '../../../core/services/base-data.service';
import {Subscription} from 'rxjs';
import {OfferService} from '../../../data/services/offer.service';
import {Offer} from '../../../data/models/offer.model';
import {isDigital} from '../../../core/utils/offer-utils';
import {ActivatedRoute} from '@angular/router';
import {LoginService} from '../../../core/components/login/login.service';
import {UserData} from '../../../data/models/user-data.model';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {BaseData} from '../../../data/models/base-data.model';
import {OfferPrice} from '../../../data/models/price.model';
import {LoginComponent} from '../../../core/components/login/login.component';
import {ConfigService} from "../../../core/services/config.service";
import {CssPropertiesEnum} from "../../../core/models/css-properties.enum";
import {VHostService} from "../../../core/services/v-host.service";
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {MoveService} from "../../../data/services/move.service";
import {DatePipe, DecimalPipe, isPlatformBrowser} from '@angular/common';
import {SubExtendedDataField} from "../../../data/models/sub-extended-data-field";
import {GtmEnum} from "../../../core/models/gtm.enum";
import {ImageService} from "../../../core/services/image.service";
import {StepperOrientation} from "@angular/cdk/stepper";
import {MatStepper} from "@angular/material/stepper";
import {ClientType} from "../../../data/models/client-type.model";
import {DateAdapter} from '@angular/material/core';
import {MatButton} from "@angular/material/button";
import {HouseNoPipe} from "../../../core/pipes/houseno.pipe";
import {HouseNo} from "../../../data/models/houseno/houseno";
import {FaIconLibrary, FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {faEuro} from '@fortawesome/free-solid-svg-icons/faEuro';
import {BankAccountPipe} from '../../../core/pipes/bank-account.pipe';
import {Address} from "../../../data/models/address.model";
import {OrderProcessingService} from "../../../data/services/order-processing.service";
import {Person} from "../../../data/models/person.model";
import {OrderTrackingService} from "../../../core/services/order-tracking.service";
import {DigitalOfferComponent} from "../offer-card/digital-offer/digital-offer.component";
import {PrintOfferComponent} from "../offer-card/print-offer/print-offer.component";
import {LoadingSpinnerComponent} from "../../../core/components/loading-spinner/loading-spinner.component";
import {GeneralErrorsComponent} from "../../../core/components/general-errors/general-errors.component";

@Component({
  selector: 'app-offer-detail',
  templateUrl: './offer-detail.component.html',
  styleUrls: ['./offer-detail.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButton,
    FontAwesomeModule,
    DigitalOfferComponent,
    PrintOfferComponent,
    LoadingSpinnerComponent,
    GeneralErrorsComponent
  ],
  providers: [
    DatePipe,
    DecimalPipe,
    HouseNoPipe,
    BankAccountPipe,
  ],
  standalone: true
})
export class OfferDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  protected orderProcessService = inject(OrderProcessingService);
  private baseDataService = inject(BaseDataService);
  private loginService = inject(LoginService);
  private offerService = inject(OfferService);
  private hupSubscriptionsService = inject(HupSubscriptionsService);
  private route = inject(ActivatedRoute);
  protected translationService = inject(TranslationService);
  private configService = inject(ConfigService);
  protected vHostService = inject(VHostService);
  private orderTrackingService = inject(OrderTrackingService);
  private navigationService = inject(NavigationService);
  private moveService = inject(MoveService);
  private platformId = inject(PLATFORM_ID);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);

  @ViewChild('login') loginComponent: LoginComponent;
  @ViewChild('stepper') stepper: MatStepper;

  askForPaymentAccountHolder: boolean = false;
  countries: BaseData[] = [];
  optinResult: Map<number, boolean>;

  CssPropertiesEnum = CssPropertiesEnum;

  private colAmount: number = 5;
  offer = signal<Offer>(null);
  isEditable: boolean = true;

  isDigitalOffer(): boolean {
    return isDigital(this.offer()?.offerType);
  }

  offerPrice: OfferPrice[];
  authSub: Subscription;
  isAuthenticated: boolean = false;
  errorsArr: string[] = [];

  isLoadingUserData: boolean = false;
  isLoadingView: boolean = false;
  offerFound: boolean = true;
  userData = signal<UserData>(null);

  protected clientTypes: ClientType[] = [];

  additionalData: SubExtendedDataField[] = [];
  leftSide: number = 66;
  rightSide: number = 33;

  private offerContainer: ElementRef | null = null;
  @ViewChild('offerContainer')
  set offerContainerRef(container: ElementRef | undefined) {
    this.offerContainer = container ?? null;
    if (this.offerContainer) {
      setTimeout(() => this.calculateWidth(), 0);
    }
  }

  stepperOrientation: StepperOrientation = 'horizontal';

  redirectUrl = null;
  clientNoUrl = null;
  urlParams: Map<string, string> = new Map<string, string>;
  isWinback: boolean = false;

  hideLoginBox = false;

  imageService = inject(ImageService);

  showBackToOverviewButton: boolean;
  proposedInvoiceEmail: string = '';
  isUserLoggedIn: boolean;

  public currentProductAlreadyOrdered = signal(false); //Bezieht der Nutzer das Produkt schon?
  private alreadyOrderedCheckEnabled: boolean = true; //Darf das überhaupt geprüft werden? (P5W-4591)

  protected readonly document = typeof document === 'object' ? document : null; // doc n.a. in SSR

  constructor() {
    const faLibrary = inject(FaIconLibrary);

    faLibrary.addIcons(
      faEuro
    );
    this.dateAdapter.getFirstDayOfWeek = () => 1;
    this.isLoadingView = true;
    this.handleConfigs();
    this.clientTypes = this.baseDataService.getBaseData('clienttypes');
  }

  private handleConfigs() {
      this.hideLoginBox = this.configService.getConfig('offer.hideLoginWhileOffer')?.asBoolean();

      this.showBackToOverviewButton = this.configService.getConfig('show.backToOverView.button')?.asBoolean();

      // P5W-4591: Ohne Eintrag oder bei ungültigem Wert bleibt die Prüfung eingeschaltet
      this.alreadyOrderedCheckEnabled = this.configService.getConfig('offer.checkProductAlreadyOrdered')?.asBoolean() ?? true;
  }

  onResize(): void {
    this.calculateWidth();
  }

  calculateWidth(): void {
    const containerWidth = this.offerContainer?.nativeElement?.offsetWidth ?? 0;
    const viewportWidth = isPlatformBrowser(this.platformId) ? window.innerWidth : 0;
    const width = containerWidth || viewportWidth;
    if (width) {
      this.colAmount = (width >= 768) ? 3 : 2;
      this.stepperOrientation = width <= 700 ? 'vertical' : 'horizontal';
      this.leftSide = width <= 850 ? 100 : 66;
      this.rightSide = width <= 850 ? 100 : 33;
    }
  }

  ngOnInit(): void {
    this.optinResult = new Map<number, boolean>();
    this.countries = this.baseDataService.getBaseData('countrycodes');

    this.handleQueryParameters();
  }

  private handleQueryParameters() {
    this.route.queryParamMap.subscribe((queryParams) => {
      this.redirectUrl = queryParams.get('redirect_URL');
      this.orderProcessService.update(state => ({
        ...state,
        redirectUrl: this.redirectUrl
      }))
    });

    this.route.queryParamMap.subscribe((queryParams) => {
        const paramsToSave = this.configService.getConfig('offer.savedQueryParams');
        paramsToSave?.value.split(',').forEach(param => {
          if (queryParams.has(param)) {
            this.urlParams.set(param, queryParams.get(param));
            this.orderProcessService.update(state => ({
              ...state,
              urlParams: this.urlParams
            }));
          }
        });
    });

    this.route.paramMap.subscribe((params) => {
      const offerId = params.get('offerId');
      if (offerId && Number.isInteger(+offerId)) {
        this.offerService.getOfferDetail(+offerId).subscribe(offer => {
          this.handleOffer(offer);
        });
      }
    });
  }

  handleOffer(offer: Offer): void {
    if (offer) {
      // Nutze setTimeout um NG0100 zu vermeiden, falls das Signal
      // asynchron während der View-Initialisierung aktualisiert wird.
      setTimeout(() => {
        this.offer.set(offer);
        this.calculateWidth();
        //Zuerst prüfen wir, ob der Nutzer ein laufendes oder ein zukünftiges Abo für dieses Objekt hat (heute als Referenzdatum)
        this.checkIfCurrentProductAlreadyOrdered(new Date());
      });

      //Sichern von der Offer am SubscriptionOrder
      this.orderProcessService.update(state => ({
        ...state,
        offer: offer
      }));

      //Falls es den User schon gibt, laden wir diesen und entnehmen alle notwendigen Details
      this.authSub = this.loginService.getAuthData().subscribe((authData) => {
        this.isAuthenticated = !!authData && (authData.token !== null);
        if (this.isAuthenticated) {
          this.loginService.getUserData().subscribe(userDataSso => {
            if (!userDataSso.userBackendId) {
              this.handleUserData(userDataSso);
            } else {
              this.moveService.getUser().subscribe({
                next: user => {
                  if (user) {
                    if (userDataSso.emailList.length > 0) {
                      user.emailList = userDataSso.emailList;
                    }
                    this.handleUserData(user);
                  }
                },
                error: () => {
                  userDataSso.person = null;
                  userDataSso.address = null;
                  userDataSso.mobileList = [];
                  userDataSso.phoneList = [];
                  this.handleUserData(userDataSso);
                }
              });
            }
          });
        }
      });

      // Das früheste/späteste Startdatum ermittelt der Zahlungs-Schritt (PaymentStepComponent) selbst.

      this.orderTrackingService.pushEvent(GtmEnum.BEGIN_CHECKOUT, offer);
    } else {
      this.offerFound = false;
    }
  }

  handleUserData(userData: UserData): void {
    this.isLoadingUserData = false;

    this.userData.set(userData);

    this.proposedInvoiceEmail = this.userData().emailList?.at(0) ?? '';

    //email updaten an dem SubscriptionOrder-Objekt
    this.orderProcessService.update(state => ({
      ...state,
      ssoEmail: this.proposedInvoiceEmail != '' ? this.proposedInvoiceEmail : state.registration.email
    }));

    this.isUserLoggedIn = this.userData() && this.userData()?.userBackendId > 0;
    if (this.userData()?.person?.initials) {
      this.userData().person.initials = this.userData().person.initials.split('.').join('');
    }
    let addressCode = this.userData().person?.addressCode;
    if (this.userData()?.person?.salutation && !this.userData()?.person.addressCode) {
      this.clientTypes?.forEach(ct => {
        if (ct.key === this.userData().person.salutation) {
          addressCode = ct.defaultAddressCodeId;
        }
      });
    }
    let country = null;
    if (!!this.userData()?.address) {
      this.countries?.forEach(_country => {
        if (_country.key === this.userData().address?.country.toUpperCase()) {
          country = _country;
        }
      });
    }

    //UserBackendId sichern, falls vorhanden
    if (this.userData()?.userBackendId > 0) {
      this.orderProcessService.update(state => ({
        ...state,
        userBackendId: this.userData().userBackendId
      }));
    }

    if (this.userData()?.userSSOId > 0) {
      this.orderProcessService.update(state => ({
        ...state,
        userSSOId: this.userData().userSSOId
      }));
    }

    if (this.offer()?.requiredAddresses?.deliveryAddressRequired && this.userData() || this.isUserLoggedIn) {
      // vorhandene Teile des SubscriptionOrder-Objekts updaten. Im address-step werden die Daten vorbefüllt
      this.orderProcessService.update(state => ({
          ...state,
          addresses: {
            ...state.addresses,
            deliveryAddress: this.getAddressFromUserData(addressCode)
          }
        })
      );
    }

    if (this.offer()?.requiredAddresses?.billingAddressRequired && !this.offer()?.requiredAddresses?.deliveryAddressRequired && this.userData()) {
      this.orderProcessService.update(state => ({
          ...state,
          addresses: {
            ...state.addresses,
            billingAddress: this.getAddressFromUserData(addressCode)
          }
        })
      );
    }

    this.orderProcessService.update(state => ({
      ...state,
      userData: this.userData()
    }));

    //Sobald die UserData geladen sind, prüfen wir, ob der Nutzer das aktuelle Produkt bereits abonniert hat.
    //Der Aufruf im Offer-Lade-Pfad läuft sonst ins Leere, weil die UserData asynchron erst danach eintreffen.
    this.checkIfCurrentProductAlreadyOrdered(new Date());
  }

  private getAddressFromUserData(addressCode?: any): any {
    const firstName = this.userData().person?.firstname ?? '';
    const lastName = this.userData().person?.lastname ?? '';
    const email = this.userData().emailList[0] ?? ''; //todo: muss auch in die Form
    const title = this.userData().person?.title ?? '';
    // addressCode ist der Schluessel selbst (person.addressCode bzw. clientType.defaultAddressCodeId)
    // und kein Objekt mit 'key' - genau so patcht ihn auch der Adress-Step. Das fruehere
    // addressCode?.key war immer undefined; die Anrede des Bestandskunden fehlte dadurch ueberall:
    // in der Uebersicht, auf der Praemienadress-Kachel und als Pflichtfeld, das die Bestellung
    // blockierte.
    const salutation = addressCode ?? '';
    const initials = this.userData().person?.initials ?? '';

    const street = this.userData().address?.street ?? '';
    const birthDay = this.userData()?.birthday ?? ''; // todo: muss auch in die Form
    const zipcode = this.userData().address?.zipcode ?? '';
    const country = this.userData().address?.country ?? '';
    const city = this.userData().address?.city ?? '';
    const district = this.userData().address?.district ?? '';
    const rawHouseNo = this.userData().address?.houseno;
    const houseNo = rawHouseNo ? HouseNo.getHouseNumber(rawHouseNo) : '';
    const webId = this.userData().address?.webId ?? 0;
    const backendId = this.userData().address?.backendId ?? 0;
    const userBackendId = this.userData().address?.userBackendId ?? 0;
    const userSsoId = this.userData().address?.userSSOId ?? 0;

    const userData = {birthDay: birthDay, email: email }
    const person = new Person(title, salutation, firstName, lastName, initials);
    const address = new Address(street, houseNo, zipcode, city, country, webId, backendId, userBackendId, userSsoId, district);

    return {
      userData,
      person,
      address
    }
  }

  setValidationChangeListener(): void {
    // this.addressFormGroup.get('billingAddress.showAddress').valueChanges.subscribe(value => {
    //   this.setValidatorsAtForm('billingAddress', value);
    // });
    //
    // this.addressFormGroup.get('bonusAddress.showAddress').valueChanges.subscribe(value => {
    //   this.setValidatorsAtForm('bonusAddress', value);
    // });
    //
    // this.paymentFormGroup.get('formOfPayment').valueChanges.subscribe(value => {
    //   this.handleFormOfPaymentChange(value);
    // });
    //
    // this.paymentFormGroup.get('paymentFrequency').valueChanges.subscribe(value => {
    //   this.selectedPaymentFrequency = value;
    // });
    //
    // this.paymentFormGroup.get('startdate').valueChanges.subscribe(value => {
    //   this.selectedStartDate = value;
    // });
    //
    // this.loginFormGroup.get('email').valueChanges.subscribe(value => {
    //   this.addressFormGroup.get('deliveryAddress.userData().email').patchValue(value);
    //   this.addressFormGroup.get('billingAddress.userData().email').patchValue(value);
    //   this.addressFormGroup.get('bonusAddress.userData().email').patchValue(value);
    //   if(value?.length >= 0) {
    //     this.proposedInvoiceEmail = value;
    //   }
    // });
    // this.addressFormGroup.get('email').valueChanges.subscribe(value => {
    //   if((this.proposedInvoiceEmail?.length == 0 || value.toString().startsWith(this.proposedInvoiceEmail)) && value?.length >= 0) {
    //     this.proposedInvoiceEmail = value;
    //   }
    // });
  }

  isString(x): boolean {
    return Object.prototype.toString.call(x) === '[object String]';
  }

  onClickEvent(): void {
    this.loginComponent.onClickEvent();
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.orderProcessService.resetState();
  }

  backToOverview(): void {
    this.navigationService.navigateTo(NavigationCommand.OFFER, null, true);
  }

  ngAfterViewInit(): void {
    this.isWinback = false;
    console.debug('View is being initialized');
    setTimeout(() => {
      console.debug('View is fully loaded');
      this.route.queryParamMap.subscribe((queryParams) => {
        this.clientNoUrl = queryParams.get('clientNo');
        if (this.clientNoUrl) {
          this.orderProcessService.update(state => ({
            ...state,
            clientNoUrl: this.clientNoUrl
          }));

          this.moveService.getUser().subscribe(user => {
            this.isWinback = true;
            this.isLoadingUserData = false;
            this.isAuthenticated = !!user;
            this.handleUserData(user);
          });
        }
      });
      if (isPlatformBrowser(this.platformId)) {
        this.isLoadingView = false;
        setTimeout(() => this.calculateWidth(), 0);
      }
    }, 0);
  }

  private checkIfCurrentProductAlreadyOrdered(referenceDate: Date) {
    //Prüfung abgeschaltet: der Nutzer gilt nicht als Bezieher - kein Hinweis, kein Sprung im Stepper
    if (!this.alreadyOrderedCheckEnabled) {
      return;
    }
    const startDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate()).toISOString().split('T')[0];
    if (this.userData()?.userBackendId && this.offer()?.product.productCode) {
      this.hupSubscriptionsService.checkIfCurrentProductAlreadyOrdered(this.userData().userBackendId, this.offer().product.productCode, startDate).subscribe({
        next: orderedProduct => {
          this.currentProductAlreadyOrdered.set(orderedProduct.exists);
        },
        complete: () => {
          console.log('Finished checking if current product is already ordered.');
        },
        error: error => {
          console.error('Fehler bei der Prüfung, ob das aktuelle Produkt bereits abonniert ist:', error);
        }
      });
    }
  }
}
